import React, { useEffect, useRef } from 'react';
import type { Craft } from '../data/crafts';
type CraftMarqueeProps = {
  items: Craft[];
  /** 'left' scrolls content right-to-left, 'right' scrolls left-to-right. */
  direction?: 'left' | 'right';
  /** Pixels per second. */
  speed?: number;
  label: string;
};

/** Rotating color tints so the strip reads as a colorful toy pack, not a beige row. */
const tints = ['bg-brandSoft', 'bg-sunSoft', 'bg-flameSoft', 'bg-goSoft', 'bg-skySoft'];
const edgeFade = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)',
  maskImage: 'linear-gradient(to right, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)'
} as React.CSSProperties;
export function CraftMarquee({
  items,
  direction = 'left',
  speed = 58,
  label
}: CraftMarqueeProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const pausedRef = useRef(false);
  const resumeRef = useRef<number | null>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const half = () => el.scrollWidth / 2;
    if (direction === 'right') {
      el.scrollLeft = half();
    }
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const delta = Math.min(now - last, 64) / 1000;
      last = now;
      if (!pausedRef.current) {
        const loop = half();
        let next = el.scrollLeft + (direction === 'left' ? 1 : -1) * speed * delta;
        if (next >= loop) next -= loop;
        if (next <= 0) next += loop;
        el.scrollLeft = next;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [direction, speed]);
  useEffect(() => {
    return () => {
      if (resumeRef.current) window.clearTimeout(resumeRef.current);
    };
  }, []);
  const pause = () => {
    pausedRef.current = true;
    if (resumeRef.current) window.clearTimeout(resumeRef.current);
  };
  const scheduleResume = () => {
    if (resumeRef.current) window.clearTimeout(resumeRef.current);
    resumeRef.current = window.setTimeout(() => {
      pausedRef.current = false;
    }, 1800);
  };
  const loopItems = [...items, ...items];
  return <div className="w-full overflow-hidden" role="region" aria-label={label} style={edgeFade} onMouseEnter={pause} onMouseLeave={scheduleResume} data-id="element-43" data-mp="210eqig32i9n4">
      <div ref={scrollerRef} className="no-scrollbar flex w-full gap-3 overflow-x-auto overscroll-x-contain px-5 py-1 sm:gap-4 sm:px-6" style={{
      scrollBehavior: 'auto'
    }} onPointerDown={pause} onPointerUp={scheduleResume} onPointerCancel={scheduleResume} onTouchStart={pause} onTouchEnd={scheduleResume} onWheel={() => {
      pause();
      scheduleResume();
    }} data-id="element-44" data-mp="18bg77fn7l1g8">
        {loopItems.map((craft, index) => <figure key={`${craft.id}-${index}`} aria-hidden={index >= items.length} className="w-[clamp(150px,42vw,232px)] flex-none overflow-hidden rounded-card border border-hairline bg-white p-1.5 shadow-paper" data-id="element-45" data-mp="318bsyr2ke20a" data-mpi={index}>
            <div className={`overflow-hidden rounded-[11px] ${tints[index % tints.length]}`} data-id="element-46" data-mp="2w877snqpoj3q">
              <img src={craft.src} alt={index >= items.length ? '' : craft.alt} loading="lazy" draggable={false} className="aspect-[4/5] w-full select-none object-cover mix-blend-multiply" data-id="element-47" data-mp="3jxrspc6fdqfn" />
            </div>
          </figure>)}
      </div>
    </div>;
}